import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { ActivityRouteMap } from '../components/ActivityRouteMap';
import {
  disconnectStrava,
  getStravaActivities,
  getStravaStatus,
  startStravaConnect,
  type StravaActivity,
  type StravaAthlete,
} from '../services/strava';

type RouteCoordinate = [number, number];

type DisplayActivity = {
  id: string;
  name: string;
  date: string;
  distance: string;
  duration: string;
  elevation: string;
  type: string;
  score: number | null;
  green: string | null;
  routeCoordinates: RouteCoordinate[];
  source: 'demo' | 'strava';
};

const demoActivities: DisplayActivity[] = [
  {
    id: 'run-gbk',
    name: 'Morning Run GBK',
    date: '22 Jul 2026 · 06.30',
    distance: '8,45 km',
    duration: '54:32',
    elevation: '82 m',
    score: 78,
    green: '62%',
    type: 'Run',
    source: 'demo',
    routeCoordinates: [
      [106.8003, -6.2183], [106.7995, -6.2167], [106.7999, -6.2149],
      [106.8012, -6.2139], [106.8030, -6.2136], [106.8047, -6.2144],
      [106.8057, -6.2161], [106.8055, -6.2181], [106.8046, -6.2195],
      [106.8029, -6.2203], [106.8013, -6.2200], [106.8003, -6.2183],
    ],
  },
  {
    id: 'ride-menteng',
    name: 'Evening Ride Menteng',
    date: '20 Jul 2026 · 16.45',
    distance: '15,2 km',
    duration: '01:12:10',
    elevation: '120 m',
    score: 72,
    green: '55%',
    type: 'Ride',
    source: 'demo',
    routeCoordinates: [
      [106.8292, -6.1965], [106.8274, -6.1952], [106.8255, -6.1934],
      [106.8238, -6.1908], [106.8250, -6.1887], [106.8280, -6.1880],
      [106.8310, -6.1892], [106.8331, -6.1914], [106.8327, -6.1942],
      [106.8312, -6.1961], [106.8292, -6.1965],
    ],
  },
  {
    id: 'walk-monas',
    name: 'Jalan Pagi Monas',
    date: '18 Jul 2026 · 07.00',
    distance: '4,1 km',
    duration: '48:20',
    elevation: '20 m',
    score: 81,
    green: '71%',
    type: 'Walk',
    source: 'demo',
    routeCoordinates: [
      [106.8264, -6.1768], [106.8242, -6.1760], [106.8230, -6.1742],
      [106.8235, -6.1720], [106.8255, -6.1709], [106.8281, -6.1711],
      [106.8301, -6.1724], [106.8305, -6.1747], [106.8292, -6.1764],
      [106.8264, -6.1768],
    ],
  },
];

function formatDistance(meters: number): string {
  return `${(meters / 1000).toLocaleString('id-ID', { maximumFractionDigits: 2 })} km`;
}

function formatDuration(seconds: number): string {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const remainingSeconds = seconds % 60;
  return hours > 0
    ? `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(remainingSeconds).padStart(2, '0')}`
    : `${String(minutes).padStart(2, '0')}:${String(remainingSeconds).padStart(2, '0')}`;
}

function formatDate(dateValue: string): string {
  const date = new Date(dateValue.replace(/Z$/, ''));
  return new Intl.DateTimeFormat('id-ID', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
}

function decodePolyline(encoded: string): Array<[number, number]> {
  const points: Array<[number, number]> = [];
  let index = 0;
  let latitude = 0;
  let longitude = 0;

  while (index < encoded.length) {
    let result = 0;
    let shift = 0;
    let byte: number;
    do {
      byte = encoded.charCodeAt(index++) - 63;
      result |= (byte & 0x1f) << shift;
      shift += 5;
    } while (byte >= 0x20);
    latitude += result & 1 ? ~(result >> 1) : result >> 1;

    result = 0;
    shift = 0;
    do {
      byte = encoded.charCodeAt(index++) - 63;
      result |= (byte & 0x1f) << shift;
      shift += 5;
    } while (byte >= 0x20);
    longitude += result & 1 ? ~(result >> 1) : result >> 1;
    points.push([latitude / 1e5, longitude / 1e5]);
  }

  return points;
}

function polylineToRouteCoordinates(encoded: string | null): RouteCoordinate[] {
  if (!encoded) return [];
  return decodePolyline(encoded).map(([latitude, longitude]) => [longitude, latitude]);
}

function toDisplayActivity(activity: StravaActivity): DisplayActivity {
  return {
    id: activity.id,
    name: activity.name,
    date: formatDate(activity.startDateLocal),
    distance: formatDistance(activity.distanceMeters),
    duration: formatDuration(activity.movingTimeSeconds),
    elevation: `${Math.round(activity.totalElevationGain)} m`,
    type: activity.sportType,
    score: null,
    green: null,
    routeCoordinates: polylineToRouteCoordinates(activity.summaryPolyline),
    source: 'strava',
  };
}

function activityIcon(type: string): string {
  const normalized = type.toLowerCase();
  if (normalized.includes('ride') || normalized.includes('cycle')) return '🚲';
  if (normalized.includes('walk') || normalized.includes('hike')) return '🚶';
  return '🏃';
}

export function ActivityPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [athlete, setAthlete] = useState<StravaAthlete | null>(null);
  const [connected, setConnected] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activities, setActivities] = useState<DisplayActivity[]>(demoActivities);
  const [selectedId, setSelectedId] = useState(demoActivities[0].id);

  const selected = activities.find((activity) => activity.id === selectedId) || activities[0];

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const status = await getStravaStatus();
        if (cancelled) return;
        setConnected(status.connected);
        setAthlete(status.athlete || null);
        if (status.connected) {
          const result = await getStravaActivities();
          if (cancelled) return;
          const realActivities = result.activities.map(toDisplayActivity);
          setAthlete(result.athlete);
          setActivities(realActivities.length > 0 ? realActivities : []);
          setSelectedId(realActivities[0]?.id || '');
        }
      } catch (caught) {
        if (!cancelled) setError(caught instanceof Error ? caught.message : 'Gagal memuat Strava.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    void load();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const state = searchParams.get('strava');
    if (state === 'error') {
      const reason = searchParams.get('reason');
      setError(reason === 'activity_scope_not_granted'
        ? 'Izin membaca aktivitas belum diberikan di Strava.'
        : 'Koneksi Strava belum berhasil. Periksa konfigurasi dan coba lagi.');
    }
    if (state) {
      const next = new URLSearchParams(searchParams);
      next.delete('strava');
      next.delete('reason');
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  async function connect() {
    setLoading(true);
    setError(null);
    try {
      const authorizeUrl = await startStravaConnect();
      window.location.assign(authorizeUrl);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Koneksi Strava belum dapat dimulai.');
      setLoading(false);
    }
  }

  async function disconnect() {
    setLoading(true);
    setError(null);
    try {
      await disconnectStrava();
      setConnected(false);
      setAthlete(null);
      setActivities(demoActivities);
      setSelectedId(demoActivities[0].id);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Gagal memutuskan Strava.');
    } finally {
      setLoading(false);
    }
  }

  const athleteName = [athlete?.firstname, athlete?.lastname].filter(Boolean).join(' ');

  return (
    <div className="app-page activity-page">
      <header className="app-page__header">
        <div>
          <span className="eyebrow">Aktivitas</span>
          <h1>Analisis rute dalam konteks lingkungan.</h1>
          {connected && <p className="strava-connected-note">Terhubung sebagai {athleteName || 'atlet Strava'}.</p>}
        </div>
        <div className="strava-actions">
          {connected ? (
            <button className="button button--ghost button--small" type="button" onClick={() => void disconnect()} disabled={loading}>Putuskan Strava</button>
          ) : (
            <button className="button button--primary button--small" type="button" onClick={() => void connect()} disabled={loading}>{loading ? 'Memeriksa Strava...' : 'Hubungkan Strava'}</button>
          )}
        </div>
      </header>

      {error && <div className="strava-message strava-message--error">{error}</div>}
      {loading && <div className="strava-message">Memuat koneksi dan aktivitas Strava...</div>}

      <section className="activity-summary-grid">
        <article className="activity-summary-card activity-summary-card--green"><span>{connected ? activities.length : 3}</span><div><strong>Aktivitas ditampilkan</strong><small>{connected ? 'Data akun Strava' : 'Mode demo lokal'}</small></div></article>
        <article className="activity-summary-card"><span>{connected ? 'Live' : '77'}</span><div><strong>{connected ? 'Koneksi aktif' : 'Rata-rata kenyamanan'}</strong><small>{connected ? 'Session-only' : 'Nyaman'}</small></div></article>
        <article className="activity-summary-card"><span>{connected ? '10' : '63%'}</span><div><strong>{connected ? 'Maksimal aktivitas' : 'Paparan area hijau'}</strong><small>{connected ? 'Permintaan terbaru' : 'Rata-rata rute'}</small></div></article>
      </section>

      <div className="activity-layout">
        <section className="activity-list-panel">
          <div className="content-section__heading"><div><span className="eyebrow">Riwayat</span><h2>Aktivitas terakhir</h2></div></div>
          <div className="activity-list">
            {!loading && activities.length === 0 && <p>Belum ada aktivitas yang dapat dibaca dari akun Strava ini.</p>}
            {activities.map((activity) => (
              <button className={`activity-item activity-item--button ${activity.id === selectedId ? 'is-active' : ''}`} key={activity.id} type="button" onClick={() => setSelectedId(activity.id)}>
                <div className="activity-item__icon">{activityIcon(activity.type)}</div>
                <div className="activity-item__content"><h3>{activity.name}</h3><small>{activity.date}</small><div><span>{activity.distance}</span><span>{activity.duration}</span><span>{activity.elevation} elevasi</span></div></div>
                <div className="activity-item__score">{activity.score ?? '—'}</div>
              </button>
            ))}
          </div>
        </section>

        {selected && (
          <section className="route-analysis-card">
            <div className="route-analysis-card__header">
              <div>
                <span className="eyebrow">{selected.source === 'strava' ? 'Aktivitas Strava' : selected.name}</span>
                <h2>{selected.source === 'strava' ? selected.name : 'Rute cukup nyaman dan hijau'}</h2>
              </div>
              <strong>{selected.score ?? '—'}</strong>
            </div>

            {selected.routeCoordinates.length >= 2 ? (
              <ActivityRouteMap
                key={selected.id}
                coordinates={selected.routeCoordinates}
                activityName={selected.name}
              />
            ) : (
              <div className="activity-route-empty">
                <span>⌖</span>
                <div><strong>Geometri rute tidak tersedia</strong><p>Aktivitas ini tidak menyertakan summary polyline, biasanya karena pengaturan privasi atau jenis aktivitas.</p></div>
              </div>
            )}

            <div className="route-stats">
              <div><strong>{selected.distance}</strong><span>Jarak</span></div>
              <div><strong>{selected.duration}</strong><span>Durasi</span></div>
              <div><strong>{selected.elevation}</strong><span>Elevasi</span></div>
              <div><strong>{selected.green || 'Belum'}</strong><span>Analisis hijau</span></div>
            </div>
            <div className="route-story">
              <h3>{selected.source === 'strava' ? 'Rute mengikuti titik Strava' : 'Cerita di balik rute'}</h3>
              <p>{selected.source === 'strava'
                ? `Peta 2D digambar dari ${selected.routeCoordinates.length.toLocaleString('id-ID')} titik summary polyline Strava. Comfort Score dan paparan area hijau belum ditampilkan sebelum analisis lingkungan dijalankan.`
                : 'Garis oranye mengikuti titik koordinat demo pada kawasan sebenarnya. Marker mulai dan selesai membantu membaca arah aktivitas.'}</p>
            </div>
            <Link className="button button--primary button--full" to="/app/story">Buat Story dari aktivitas ini</Link>
          </section>
        )}
      </div>
    </div>
  );
}
